import clsx from 'clsx';
import React, { ChangeEvent, CSSProperties, useEffect, useRef } from 'react';

import './BayCheckbox.scss';

import BayTooltip from '../BayTooltip';

// Plain div forwarding className/style/children -- the styling lives in
// BayCheckbox.scss now, keyed off the `bay-checkbox` class every consumer
// (this component and TopNav/components/RadioGroupComponents.jsx) already
// includes in the className it passes in.
interface CheckboxInputProps {
	className?: string;
	style?: CSSProperties;
	children?: React.ReactNode;
}

export const CheckboxInput = ({ className, style, children }: CheckboxInputProps) => (
	<div className={className} style={style}>
		{children}
	</div>
);

export interface BayCheckboxProps {
	id?: string;
	name?: string;
	disabled?: boolean;
	isChecked?: boolean;
	isRadio?: boolean;
	onChangeFunc?: (event: ChangeEvent<HTMLInputElement>) => void;
	label?: React.ReactNode;
	value?: string;
	className?: string;
	style?: CSSProperties;
	checkBoxTooltipContent?: string;
	checkBoxTooltipPlacement?: 'top' | 'right' | 'bottom' | 'left';
	isIndeterminate?: boolean;
	labelStyle?: CSSProperties;
}

const BayCheckbox: React.FC<BayCheckboxProps> = ({
	id,
	name,
	disabled = false,
	isChecked = false,
	isRadio = false,
	onChangeFunc = () => {},
	label,
	value,
	className,
	style,
	checkBoxTooltipContent = '',
	checkBoxTooltipPlacement = 'top',
	isIndeterminate = false,
	labelStyle = {},
}) => {
	const cxClassNames = clsx(['bay-checkbox', 'checkbox-form', className]);
	const inputRef = useRef<HTMLInputElement>(null);

	// `indeterminate` is a DOM-only property, so set it directly on the input.
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.indeterminate = !isRadio && isIndeterminate && !isChecked;
		}
	}, [isRadio, isIndeterminate, isChecked]);

	const toolTip = (children: React.ReactNode): React.ReactNode => {
		let el = children;
		if (checkBoxTooltipContent !== '') {
			el = (
				<BayTooltip
					overlay={checkBoxTooltipContent}
					placement={checkBoxTooltipPlacement}
				>
					<div>{children}</div>
				</BayTooltip>
			);
		}
		return el;
	};

	return (
		<CheckboxInput className={cxClassNames} style={style}>
			<label
				className={`d-flex align-items-center text-truncate ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
				htmlFor={`checkbox_${id ? id : ''}`}
			>
				{toolTip(
					<input
						ref={inputRef}
						checked={isChecked}
						className="bay-check-input"
						disabled={disabled}
						id={`${id ? `checkbox_${id}` : ''}`}
						name={name}
						type={isRadio ? 'radio' : 'checkbox'}
						value={value}
						onChange={onChangeFunc}
					/>,
				)}
				{label && <span className="checkbox-label" style={labelStyle}>{label}</span>}
			</label>
		</CheckboxInput>
	);
};

export default BayCheckbox;
